/* eslint-disable complexity */
import { ClaimLimitType, ClaimTicketStatus, CosmeticEventSource, GiftmasQuestType, QuestClaimStatus, RewardType, StakingPlan, StakingStatus, WheelType } from 'src/models';
import { AllowListStatus, Blockchain, BoxConfigRefType, BoxType, CosmeticRarity, CosmeticRefType, CosmeticStatus, CosmeticType, HeroStatus, HeroTrophy, NftItemRarity, NftRefType, NftSkinItemRarity, NtfHeroClassType, ProjectStatus, TradingQuestType } from 'src/utils/types';
import { CurrencyType, serverCurrencyToSystemCurrency } from '@wolffun/system-currency';
import { webImageUri } from 'src/utils/uri';
import { createSelectorHook, useDispatch } from 'react-redux';

/** Typed selector */
export const useTypedSelector = createSelectorHook();
/** Typed dispatcher */

export const useTypedDispatch = useDispatch;
export const waitForMs = ms => new Promise(resolve => setTimeout(resolve, ms, undefined));
export const parseNftHero = hero => {
  const {
    id,
    ownerId,
    tokenId,
    refId,
    refType,
    systemCurrency,
    hp,
    dmg,
    heroTypeId,
    heroRarity,
    skinId,
    skinRarity,
    status,
    marketStatus,
    level,
    trophyClass,
    imageAvatar,
    imageFull,
    name,
    skinName,
    rarity,
    heroRole,
    ownerAddress,
    battleCap,
    battleCapMax,
    thcBonus,
    dailyTHCBattleConfig
  } = hero;
  return {
    id: typeof id === 'string' ? id : '',
    ownerId: typeof ownerId === 'string' ? ownerId : '',
    tokenId: typeof tokenId === 'string' ? tokenId : '',
    refId: typeof refId === 'string' ? refId : '',
    refType: typeof refType === 'string' ? refType : NftRefType.HERO,
    systemPrice: serverCurrencyToSystemCurrency(systemCurrency),
    hp: typeof hp === 'number' ? hp : 0,
    dmg: typeof dmg === 'number' ? dmg : 0,
    heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
    heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
    skinId: typeof skinId === 'number' ? skinId : 0,
    skinRarity: typeof skinRarity === 'number' ? skinRarity : NftSkinItemRarity.NORMAL,
    status: typeof status === 'number' ? status : HeroStatus.DEFAULT,
    marketStatus: typeof marketStatus === 'number' ? marketStatus : 0,
    level: typeof level === 'number' ? level : 0,
    trophy: typeof trophyClass === 'number' ? trophyClass : HeroTrophy.H,
    imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
    imageFull: typeof imageFull === 'string' ? imageFull : '',
    name: typeof name === 'string' ? name : '',
    skinName: typeof skinName === 'string' ? skinName : '',
    rarity: typeof rarity === 'number' ? rarity : NftItemRarity.COMMON,
    heroRole: typeof heroRole === 'number' ? heroRole : NtfHeroClassType.TANK,
    ownerAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    battleCap: typeof battleCap === 'number' ? battleCap : 0,
    battleCapMax: typeof battleCapMax === 'number' ? battleCapMax : 0,
    thcBonus: typeof thcBonus === 'number' ? thcBonus : 0,
    dailyTHCBattleConfig: typeof dailyTHCBattleConfig === 'number' ? dailyTHCBattleConfig : 0
  };
};
export const parseHeroRental = hero => {
  const {
    id,
    ownerId,
    tokenId,
    refId,
    refType,
    rentOutInfo,
    hp,
    dmg,
    heroTypeId,
    heroRarity,
    skinId,
    skinRarity,
    status,
    marketStatus,
    level,
    trophyClass,
    imageAvatar,
    imageFull,
    name,
    skinName,
    rarity,
    heroRole,
    ownerAddress,
    battleCap,
    battleCapMax,
    thcBonus,
    dailyTHCBattleConfig
  } = hero;
  return {
    id: typeof id === 'string' ? id : '',
    ownerId: typeof ownerId === 'string' ? ownerId : '',
    tokenId: typeof tokenId === 'string' ? tokenId : '',
    refId: typeof refId === 'string' ? refId : '',
    refType: typeof refType === 'string' ? refType : NftRefType.HERO,
    systemPrice: rentOutInfo ? serverCurrencyToSystemCurrency(rentOutInfo.price) : undefined,
    hp: typeof hp === 'number' ? hp : 0,
    dmg: typeof dmg === 'number' ? dmg : 0,
    heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
    heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
    skinId: typeof skinId === 'number' ? skinId : 0,
    skinRarity: typeof skinRarity === 'number' ? skinRarity : NftSkinItemRarity.NORMAL,
    status: typeof status === 'number' ? status : HeroStatus.DEFAULT,
    marketStatus: typeof marketStatus === 'number' ? marketStatus : 0,
    level: typeof level === 'number' ? level : 0,
    trophy: typeof trophyClass === 'number' ? trophyClass : HeroTrophy.H,
    imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
    imageFull: typeof imageFull === 'string' ? imageFull : '',
    name: typeof name === 'string' ? name : '',
    skinName: typeof skinName === 'string' ? skinName : '',
    rarity: typeof rarity === 'number' ? rarity : NftItemRarity.COMMON,
    heroRole: typeof heroRole === 'number' ? heroRole : NtfHeroClassType.TANK,
    ownerAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    battleCap: typeof battleCap === 'number' ? battleCap : 0,
    battleCapMax: typeof battleCapMax === 'number' ? battleCapMax : 0,
    thcBonus: typeof thcBonus === 'number' ? thcBonus : 0,
    dailyTHCBattleConfig: typeof dailyTHCBattleConfig === 'number' ? dailyTHCBattleConfig : 0,
    rentOutInfo: rentOutInfo ? {
      rentBattles: typeof rentOutInfo.rentBattles === 'number' ? rentOutInfo.rentBattles : 0
    } : undefined
  };
};
export const parseStakingConfig = config => {
  const {
    version,
    beginDay,
    endDay,
    maxPool,
    minStakeAmount,
    thgPerHero,
    minClaimAmount,
    maxClaimAmount,
    cooldownClaim,
    rewardPool,
    plans,
    promotion
  } = config || {};
  const defaultStakingPlan = {
    id: 0,
    lockDuration: 0,
    minHeroes: 0,
    name: '',
    privateTicketRewardWeight: 0,
    thgRewardWeight: 0
  };
  const defaultPlans = {
    [StakingPlan.FLEXIBLE]: defaultStakingPlan,
    [StakingPlan.ONE_YEAR]: defaultStakingPlan,
    [StakingPlan.SIX_MONTHS]: defaultStakingPlan
  };
  return {
    version: typeof version === 'number' ? version : 0,
    beginDate: typeof beginDay === 'string' && !isNaN(Date.parse(beginDay)) ? Date.parse(beginDay) : undefined,
    countdownClaim: typeof cooldownClaim === 'number' ? cooldownClaim : 0,
    maxClaimAmount: serverCurrencyToSystemCurrency(maxClaimAmount),
    maxPool: serverCurrencyToSystemCurrency(maxPool),
    minStakeAmount: serverCurrencyToSystemCurrency(minStakeAmount),
    thgPerHero: serverCurrencyToSystemCurrency(thgPerHero),
    minClaimAmount: serverCurrencyToSystemCurrency(minClaimAmount),
    endDate: typeof endDay === 'string' && !isNaN(Date.parse(endDay)) ? Date.parse(endDay) : undefined,
    rewardPool: typeof rewardPool === 'number' ? rewardPool : 0,
    plans: Array.isArray(plans) ? plans.reduce((result, plan) => {
      const {
        id
      } = plan || {};

      if (typeof id === 'number') {
        result[id] = parseStakingPlanConfig(plan);
      }

      return result;
    }, defaultPlans) : defaultPlans,
    promotion: {
      begin: typeof (promotion === null || promotion === void 0 ? void 0 : promotion.begin) === 'string' && !isNaN(Date.parse(promotion.begin)) ? Date.parse(promotion.begin) : undefined,
      end: typeof (promotion === null || promotion === void 0 ? void 0 : promotion.end) === 'string' && !isNaN(Date.parse(promotion.end)) ? Date.parse(promotion.end) : undefined,
      rate: typeof (promotion === null || promotion === void 0 ? void 0 : promotion.rate) === 'number' ? promotion.rate : 0
    }
  };
};
export const parseStakingPlanConfig = plan => {
  return {
    id: typeof (plan === null || plan === void 0 ? void 0 : plan.id) === 'number' ? plan.id : 0,
    lockDuration: typeof (plan === null || plan === void 0 ? void 0 : plan.lockDuration) === 'number' ? plan.lockDuration : -1,
    minHeroes: typeof (plan === null || plan === void 0 ? void 0 : plan.minHeroes) === 'number' ? plan.minHeroes : -1,
    name: typeof (plan === null || plan === void 0 ? void 0 : plan.name) === 'string' ? plan.name : '',
    privateTicketRewardWeight: typeof (plan === null || plan === void 0 ? void 0 : plan.privateTicketRewardWeight) === 'number' ? plan.privateTicketRewardWeight : 0,
    thgRewardWeight: typeof (plan === null || plan === void 0 ? void 0 : plan.thgRewardWeight) === 'number' ? plan.thgRewardWeight : -1,
    endDepositDate: typeof (plan === null || plan === void 0 ? void 0 : plan.endDepositDay) === 'string' && !isNaN(Date.parse(plan.endDepositDay)) ? Date.parse(plan.endDepositDay) : undefined
  };
};
export const parseMyStaking = data => {
  const {
    id,
    plan,
    amount,
    stakedAt,
    unstakedAt,
    pendingReward,
    totalReward,
    privateTickets,
    pendingPrivateTickets,
    tokenIds,
    canClaimReward,
    blockchainId,
    canUnstake,
    claimableReward,
    status
  } = data || {};
  return {
    id: typeof id === 'string' ? id : '',
    plan: typeof plan === 'number' ? plan : StakingPlan.FLEXIBLE,
    amount: serverCurrencyToSystemCurrency(amount),
    stakedAt: typeof stakedAt === 'string' && !isNaN(Date.parse(stakedAt)) ? Date.parse(stakedAt) : undefined,
    unstakedAt: typeof unstakedAt === 'string' && !isNaN(Date.parse(unstakedAt)) ? Date.parse(unstakedAt) : undefined,
    pendingReward: serverCurrencyToSystemCurrency(pendingReward),
    totalReward: serverCurrencyToSystemCurrency(totalReward),
    privateTickets: serverCurrencyToSystemCurrency(privateTickets),
    pendingPrivateTickets: serverCurrencyToSystemCurrency(pendingPrivateTickets),
    tokenIds: Array.isArray(tokenIds) ? tokenIds.map(id => typeof id === 'string' ? id : '') : [],
    canClaimReward: typeof canClaimReward === 'boolean' ? canClaimReward : false,
    blockchainId: typeof blockchainId === 'number' ? blockchainId : 0,
    canUnstake: typeof canUnstake === 'boolean' ? canUnstake : false,
    claimableReward: serverCurrencyToSystemCurrency(claimableReward),
    status: typeof status === 'number' ? status : StakingStatus.PENDING
  };
};
export const parseSkinConfig = skinConfigs => {
  return skinConfigs && Array.isArray(skinConfigs) ? skinConfigs.map(e => {
    const {
      id,
      name,
      heroTypeId,
      heroRarity,
      skinRarity,
      imageAvatar,
      imageFull,
      imageSmallAvatar,
      model3D,
      scaleRatio
    } = e;
    return {
      id: typeof id === 'number' ? id : 0,
      name: typeof name === 'string' ? name : '',
      heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
      heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
      skinRarity: typeof skinRarity === 'number' ? skinRarity : NftItemRarity.COMMON,
      imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
      imageFull: typeof imageFull === 'string' ? imageFull : '',
      imageSmallAvatar: typeof imageSmallAvatar === 'string' ? imageSmallAvatar : '',
      model3D: typeof model3D === 'string' ? model3D : '',
      scaleRatio: typeof scaleRatio === 'number' ? scaleRatio : 1
    };
  }) : undefined;
};
export const parseSkinConfigEntities = skinConfigs => {
  if (skinConfigs && Array.isArray(skinConfigs.skinConfigs)) {
    const result = skinConfigs.skinConfigs.reduce((prev, e) => {
      const {
        id,
        name,
        heroTypeId,
        heroRarity,
        skinRarity,
        imageAvatar,
        imageFull,
        imageSmallAvatar,
        model3D,
        scaleRatio
      } = e;
      const item = {
        id: typeof id === 'number' ? id : 0,
        name: typeof name === 'string' ? name : '',
        heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
        heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
        skinRarity: typeof skinRarity === 'number' ? skinRarity : NftItemRarity.COMMON,
        imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
        imageFull: typeof imageFull === 'string' ? imageFull : '',
        imageSmallAvatar: typeof imageSmallAvatar === 'string' ? imageSmallAvatar : '',
        model3D: typeof model3D === 'string' ? model3D : '',
        scaleRatio: typeof scaleRatio === 'number' ? scaleRatio : 1
      };
      return { ...prev,
        [id]: item
      };
    }, {});
    return result;
  }

  return undefined;
};
export const parsePaymentToken = paymentToken => {
  const {
    id,
    name,
    symbol,
    decimals,
    contractAddress
  } = paymentToken || {};
  return {
    id: typeof id === 'string' ? id : '',
    name: typeof name === 'string' ? name : '',
    symbol: typeof symbol === 'string' ? symbol : '',
    decimals: typeof decimals === 'number' ? decimals : 0,
    contractAddress: typeof contractAddress === 'string' ? contractAddress : ''
  };
};
export const parseBoxInfo = boxInfo => {
  const {
    boxType,
    amount,
    totalAmount,
    systemPrice,
    isSale
  } = boxInfo || {};
  return {
    boxType: typeof boxType === 'number' ? boxType : 0,
    amount: typeof amount === 'number' ? amount : 0,
    totalAmount: typeof totalAmount === 'number' ? totalAmount : 0,
    systemPrice: serverCurrencyToSystemCurrency(systemPrice),
    isSale: typeof isSale === 'boolean' ? isSale : false
  };
};
export const parseGiftBoxInfo = data => {
  const {
    boxInfo,
    errorCode,
    expiredAt
  } = data || {};
  const {
    boxType,
    amount,
    totalAmount,
    systemPrice,
    isSale,
    eventLink
  } = boxInfo || {};
  return {
    boxType: typeof boxType === 'number' ? boxType : 0,
    amount: typeof amount === 'number' ? amount : 0,
    totalAmount: typeof totalAmount === 'number' ? totalAmount : 0,
    systemPrice: serverCurrencyToSystemCurrency(systemPrice),
    isSale: typeof isSale === 'boolean' ? isSale : false,
    errorCode: typeof errorCode === 'number' ? errorCode : 0,
    expiredAt: typeof expiredAt === 'number' ? expiredAt * 1000 : undefined,
    eventLink: typeof eventLink === 'string' ? eventLink : ''
  };
};
export const parseBoxConfig = boxConfig => {
  const {
    boxType,
    configs,
    skinIds
  } = boxConfig || {};
  const parsedSkinIds = Array.isArray(skinIds) ? skinIds.map(id => typeof id === 'number' ? id : undefined).filter(id => id !== undefined) : [];
  return boxConfig ? {
    configs: Array.isArray(configs) ? configs.map(e => {
      const {
        heroRarity,
        rate,
        skinRarity
      } = e;
      return {
        heroRarity,
        rate,
        skinRarity
      };
    }) : undefined,
    boxType: typeof boxType === 'number' ? boxType : 0,
    skinIds: parsedSkinIds
  } : undefined;
};
export const parseBoxConfigs = boxConfigs => {
  var _boxConfigs$boxConfig, _boxConfigs$boxConfig2, _boxConfigs$boxConfig3, _boxConfigs$boxConfig4, _boxConfigs$boxConfig5, _boxConfigs$boxConfig6;

  return boxConfigs ? {
    [BoxType.COMMON]: parseBoxConfig(boxConfigs === null || boxConfigs === void 0 ? void 0 : (_boxConfigs$boxConfig = boxConfigs.boxConfigs) === null || _boxConfigs$boxConfig === void 0 ? void 0 : _boxConfigs$boxConfig[BoxType.COMMON]),
    [BoxType.EPIC]: parseBoxConfig(boxConfigs === null || boxConfigs === void 0 ? void 0 : (_boxConfigs$boxConfig2 = boxConfigs.boxConfigs) === null || _boxConfigs$boxConfig2 === void 0 ? void 0 : _boxConfigs$boxConfig2[BoxType.EPIC]),
    [BoxType.LEGENDARY]: parseBoxConfig(boxConfigs === null || boxConfigs === void 0 ? void 0 : (_boxConfigs$boxConfig3 = boxConfigs.boxConfigs) === null || _boxConfigs$boxConfig3 === void 0 ? void 0 : _boxConfigs$boxConfig3[BoxType.LEGENDARY]),
    [BoxType.SEASON]: parseBoxConfig(boxConfigs === null || boxConfigs === void 0 ? void 0 : (_boxConfigs$boxConfig4 = boxConfigs.boxConfigs) === null || _boxConfigs$boxConfig4 === void 0 ? void 0 : _boxConfigs$boxConfig4[BoxType.SEASON]),
    [BoxType.LUNAR]: parseBoxConfig(boxConfigs === null || boxConfigs === void 0 ? void 0 : (_boxConfigs$boxConfig5 = boxConfigs.boxConfigs) === null || _boxConfigs$boxConfig5 === void 0 ? void 0 : _boxConfigs$boxConfig5[BoxType.LUNAR]),
    [BoxType.SPECIAL]: parseBoxConfig(boxConfigs === null || boxConfigs === void 0 ? void 0 : (_boxConfigs$boxConfig6 = boxConfigs.boxConfigs) === null || _boxConfigs$boxConfig6 === void 0 ? void 0 : _boxConfigs$boxConfig6[BoxType.SPECIAL])
  } : undefined;
};
export const parseBoxConfigsV2 = data => {
  const {
    version,
    boxConfigs,
    lockConfig
  } = data || {};

  if (boxConfigs && typeof boxConfigs === 'object') {
    const configs = Object.keys(boxConfigs).reduce((acc, key) => {
      const {
        boxType,
        name,
        color,
        blurColor,
        textColor,
        imgPath,
        bgPath,
        ribbonPath,
        oneLayerRate,
        twoLayersRate,
        refType,
        items,
        isLimited,
        metadata,
        additionRewards
      } = boxConfigs[key] || {};
      const parsedItems = Array.isArray(items) ? items.map(item => ({
        id: typeof (item === null || item === void 0 ? void 0 : item.id) === 'number' ? item.id : -1,
        refType: typeof (item === null || item === void 0 ? void 0 : item.id) === 'number' ? item.refType : BoxConfigRefType.HERO_SKIN
      })) : [];
      acc[Number(key)] = {
        boxType: typeof boxType === 'number' ? boxType : -1,
        name: typeof name === 'string' ? name : '',
        color: typeof color === 'string' ? color : '',
        blurColor: typeof blurColor === 'string' ? blurColor : '',
        textColor: typeof textColor === 'string' ? textColor : '',
        imgPath: typeof imgPath === 'string' ? webImageUri(imgPath) : '',
        bgPath: typeof bgPath === 'string' ? webImageUri(bgPath) : '',
        ribbonPath: typeof ribbonPath === 'string' ? webImageUri(ribbonPath) : '',
        refType: typeof refType === 'number' ? refType : -1,
        items: parsedItems,
        oneLayerRate: parseBoxConfigLayer(oneLayerRate),
        twoLayersRate: parseBoxConfigTwoLayer(twoLayersRate),
        additionRewards: Array.isArray(additionRewards) ? additionRewards : undefined,
        isLimited: typeof isLimited === 'boolean' ? isLimited : false,
        metadata: typeof metadata === 'object' ? {
          attract: typeof (metadata === null || metadata === void 0 ? void 0 : metadata.attract) === 'string' ? metadata.attract : '',
          subtitle: typeof (metadata === null || metadata === void 0 ? void 0 : metadata.subtitle) === 'string' ? metadata.subtitle : '',
          title: typeof (metadata === null || metadata === void 0 ? void 0 : metadata.title) === 'string' ? metadata.title : ''
        } : undefined
      };
      return acc;
    }, {});
    return {
      boxConfigs: configs,
      lockConfig: lockConfig ? {
        startTime: typeof lockConfig.startTime === 'string' && !isNaN(Date.parse(lockConfig.startTime)) ? Date.parse(lockConfig.startTime) : 0,
        endTime: typeof lockConfig.endTime === 'string' && !isNaN(Date.parse(lockConfig.endTime)) ? Date.parse(lockConfig.endTime) : 0,
        items: Array.isArray(lockConfig.items) ? lockConfig.items.map(e => ({
          id: typeof e.id === 'number' ? e.id : 0,
          refType: typeof e.refType === 'number' ? e.refType : 0
        })) : []
      } : undefined,
      configVer: typeof version === 'number' ? version : -1
    };
  }

  return {
    configVer: typeof version === 'number' ? version : -1
  };
};

const parseBoxConfigTwoLayer = config => {
  if (config && typeof config === 'object') {
    return Object.keys(config).reduce((acc, key) => {
      var _config$key;

      const {
        rate
      } = config[key] || {};
      acc[Number(key)] = {
        rate: typeof rate === 'number' ? rate : 0,
        child: parseBoxSkinConfig((_config$key = config[key]) === null || _config$key === void 0 ? void 0 : _config$key.child)
      };
      return acc;
    }, {});
  }

  return undefined;
};

const parseBoxConfigLayer = config => {
  if (config && typeof config === 'object') {
    return Object.keys(config).reduce((acc, key) => {
      acc[Number(key)] = parseBoxSkinConfig(config[key]);
      return acc;
    }, {});
  }

  return undefined;
};

const parseBoxSkinConfig = config => {
  if (config && typeof config === 'object') {
    return Object.keys(config).reduce((acc, key) => {
      acc[Number(key)] = typeof config[key] === 'number' ? Number(config[key]) : -1;
      return acc;
    }, {});
  }

  return undefined;
};

export const parseDescriptionInfo = info => {
  const {
    name,
    description,
    imgSmallDefaultIcon,
    role,
    atkSpeed,
    damage,
    health,
    speed
  } = info || {};
  return {
    name: typeof name === 'string' ? name : '',
    description: typeof description === 'string' ? description : '',
    imgSmallDefaultIcon: typeof imgSmallDefaultIcon === 'string' ? imgSmallDefaultIcon : '',
    role: typeof role === 'number' ? role : 0,
    atkSpeed: typeof atkSpeed === 'number' ? atkSpeed : 0,
    damage: typeof damage === 'number' ? damage : 0,
    health: typeof health === 'number' ? health : 0,
    speed: typeof speed === 'number' ? speed : 0
  };
};
export const parseDescriptionConfigs = data => {
  if (data) {
    return Object.keys(data).reduce((acc, key) => {
      acc[Number(key)] = parseDescriptionInfo(data[key]);
      return acc;
    }, {});
  }

  return undefined;
};
export const parseRentalConfigs = rentalConfigs => {
  const {
    minTHCBattles,
    minTHCPrice,
    rarityAdjust,
    feeRentOut,
    expiredOffset,
    maxTHCPrice
  } = rentalConfigs || {};
  return rentalConfigs ? {
    minTHCBattles: typeof minTHCBattles === 'number' ? minTHCBattles : 0,
    minTHCPrice: typeof minTHCPrice === 'number' ? minTHCPrice : 0,
    maxTHCPrice: typeof maxTHCPrice === 'number' ? maxTHCPrice : 0,
    rarityAdjust: {
      [NftItemRarity.COMMON]: parseNumber(rarityAdjust === null || rarityAdjust === void 0 ? void 0 : rarityAdjust[NftItemRarity.COMMON]),
      [NftItemRarity.EPIC]: parseNumber(rarityAdjust === null || rarityAdjust === void 0 ? void 0 : rarityAdjust[NftItemRarity.EPIC]),
      [NftItemRarity.LEGENDARY]: parseNumber(rarityAdjust === null || rarityAdjust === void 0 ? void 0 : rarityAdjust[NftItemRarity.LEGENDARY])
    },
    feeRentOutPer: typeof feeRentOut === 'number' ? feeRentOut : 0,
    expiredOffset: typeof expiredOffset === 'number' ? expiredOffset : 0
  } : undefined;
};
export const parseNumber = adjust => {
  return typeof adjust === 'number' ? adjust : 0;
};
export const parseLeaderboardData = leaderboardData => {
  const {
    profileInfo,
    score,
    rank,
    rewards
  } = leaderboardData || {};
  const {
    userId,
    username,
    trophy,
    trophyLevel,
    avatarId,
    guildName
  } = profileInfo || {};
  const {
    baseReward,
    bonusReward
  } = rewards || {};
  return {
    profileInfo: {
      userId: typeof userId === 'string' ? userId : '',
      username: typeof username === 'string' ? username : '',
      trophy: typeof trophy === 'number' ? trophy : 0,
      trophyLevel: typeof trophyLevel === 'number' ? trophyLevel : 0,
      avatarId: typeof avatarId === 'number' ? avatarId : 0,
      guildName: typeof guildName === 'string' ? guildName : ''
    },
    score: typeof score === 'number' ? score : 0,
    rank: typeof rank === 'number' ? rank : 0,
    rewards: {
      baseReward: baseReward ? serverCurrencyToSystemCurrency(baseReward) : {
        token: CurrencyType.GTHG,
        value: ''
      },
      bonusReward: bonusReward ? serverCurrencyToSystemCurrency(bonusReward) : {
        token: CurrencyType.GTHG,
        value: ''
      }
    }
  };
};
export const parseNftCosmetic = cosmetic => {
  const {
    id,
    createdAt,
    lastModified,
    refId,
    refType,
    tokenId,
    cosmeticInfo,
    saleInfo,
    cosmeticRarity,
    cosmeticType,
    cosmeticName,
    nftId,
    blockchainId,
    ownerAddress
  } = cosmetic;
  return {
    id: typeof id === 'string' ? id : '',
    cosmeticInfo: cosmeticInfo || null,
    createdAt: typeof createdAt === 'string' ? createdAt : '',
    lastModified: typeof lastModified === 'string' ? lastModified : '',
    refId: typeof refId === 'string' ? refId : '',
    nftId: typeof refId === 'string' ? nftId : '',
    refType: typeof refType === 'number' ? refType : CosmeticRefType.COSMETIC,
    tokenId: typeof tokenId === 'number' ? tokenId : 0,
    saleInfo: {
      price: serverCurrencyToSystemCurrency(saleInfo === null || saleInfo === void 0 ? void 0 : saleInfo.price)
    },
    ownerAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    cosmeticRarity: typeof cosmeticRarity === 'number' ? cosmeticRarity : CosmeticRarity.COMMON,
    cosmeticType: typeof cosmeticType === 'number' ? cosmeticType : undefined,
    cosmeticName: typeof cosmeticName === 'string' ? cosmeticName : '',
    blockchainId: typeof blockchainId === 'number' ? blockchainId : 0,
    userAddress: typeof ownerAddress === 'string' ? ownerAddress : ''
  };
};
export const parseCosmeticDetail = cosmetic => {
  const {
    id,
    created,
    lastModified,
    userId,
    userAddress,
    tokenId,
    cosmeticInfo,
    cosmeticRarity,
    cosmeticType,
    cosmeticName,
    sale: saleInfo,
    nftId,
    blockchainId
  } = cosmetic;
  return {
    id: typeof id === 'string' ? id : '',
    created: typeof created === 'string' ? created : '',
    lastModified: typeof lastModified === 'string' ? lastModified : '',
    userId: typeof userId === 'string' ? userId : '',
    userAddress: typeof userAddress === 'string' ? userAddress : '',
    nftId: typeof nftId === 'string' ? nftId : '',
    tokenId: typeof tokenId === 'number' ? tokenId : 0,
    cosmeticInfo: cosmeticInfo,
    saleInfo: {
      price: saleInfo !== null && saleInfo !== void 0 && saleInfo.price && saleInfo !== null && saleInfo !== void 0 && saleInfo.price.type ? serverCurrencyToSystemCurrency(saleInfo === null || saleInfo === void 0 ? void 0 : saleInfo.price) : {
        token: CurrencyType.THC,
        value: ''
      }
    },
    cosmeticRarity: typeof cosmeticRarity === 'number' ? cosmeticRarity : CosmeticRarity.COMMON,
    cosmeticType: cosmeticType,
    cosmeticName: typeof cosmeticName === 'string' ? cosmeticName : '',
    blockchainId: typeof blockchainId === 'number' ? blockchainId : 0
  };
};
export const parseCosmeticSaleHistory = data => {
  const {
    id,
    userId,
    typeId,
    source,
    type,
    timestamp,
    itemId,
    tradeInfo,
    userAddress
  } = data || {};
  return {
    id: typeof id === 'string' ? id : '',
    userId: typeof userId === 'string' ? userId : '',
    itemId: typeof itemId === 'string' ? itemId : '',
    typeId: typeof typeId === 'number' ? typeId : 0,
    source: typeof source === 'number' ? source : CosmeticEventSource.MINT,
    type: typeof type === 'number' ? type : CosmeticType.EMOTE,
    timestamp: typeof timestamp === 'number' ? timestamp : 0,
    tradeInfo: tradeInfo ? {
      address: typeof (tradeInfo === null || tradeInfo === void 0 ? void 0 : tradeInfo.address) === 'string' ? tradeInfo === null || tradeInfo === void 0 ? void 0 : tradeInfo.address : '',
      price: serverCurrencyToSystemCurrency(tradeInfo.price)
    } : {
      address: '',
      price: {
        token: CurrencyType.THC,
        value: ''
      }
    },
    userAddress: typeof userAddress === 'string' ? userAddress : ''
  };
};
export const parseProjectDetail = data => {
  const {
    id,
    name,
    imageURL,
    logoURL,
    chainName,
    symbol,
    projectStatus,
    allowlistStatus,
    tagLine,
    desc,
    shortDesc,
    websiteURL,
    documentURL,
    facebookURL,
    twitterURL,
    discordURL,
    telegramURL,
    closeAt,
    totalSale,
    pricePerToken,
    totalSold,
    totalBuyer,
    tokenSale,
    fee,
    maxAllocation,
    minAllocation,
    buyingProcessPercent,
    pTicketBusd
  } = data || {};
  const {
    saleStartTime,
    saleEndTime,
    tokenDistribution,
    initialMarketCap,
    initialTokenCirculation
  } = tokenSale || {};
  return {
    id: typeof id === 'string' ? id : '',
    name: typeof name === 'string' ? name : '',
    imageUrl: typeof imageURL === 'string' ? imageURL : '',
    logoUrl: typeof logoURL === 'string' ? logoURL : '',
    chainName: typeof chainName === 'string' ? chainName : Blockchain.BINANCE,
    symbol: typeof symbol === 'string' && !!symbol ? symbol : null,
    projectStatus: typeof projectStatus === 'string' ? projectStatus : ProjectStatus.INACTIVE,
    allowlistStatus: typeof allowlistStatus === 'string' ? allowlistStatus : AllowListStatus.CLOSED,
    tagLine: typeof tagLine === 'string' ? tagLine : '',
    desc: typeof desc === 'string' ? desc : '',
    shortDesc: typeof shortDesc === 'string' ? shortDesc : '',
    websiteUrl: typeof websiteURL === 'string' ? websiteURL : '',
    documentUrl: typeof documentURL === 'string' ? documentURL : '',
    facebookUrl: typeof facebookURL === 'string' ? facebookURL : '',
    twitterUrl: typeof twitterURL === 'string' ? twitterURL : '',
    discordUrl: typeof discordURL === 'string' ? discordURL : '',
    telegramUrl: typeof telegramURL === 'string' ? telegramURL : '',
    closeAt: typeof closeAt === 'string' && !isNaN(Date.parse(closeAt)) ? Date.parse(closeAt) : undefined,
    totalSale: serverCurrencyToSystemCurrency(totalSale),
    pricePerToken: pricePerToken ? serverCurrencyToSystemCurrency(pricePerToken) : {
      token: CurrencyType.BUSD,
      value: '0'
    },
    totalSold: serverCurrencyToSystemCurrency(totalSold),
    totalBuyer: typeof totalBuyer === 'number' ? totalBuyer : 0,
    tokenSale: {
      initialMarketCap: typeof initialMarketCap === 'string' ? Number(initialMarketCap) : 0,
      initialTokenCirculation: typeof initialTokenCirculation === 'string' ? Number(initialTokenCirculation) : 0,
      saleEndTime: typeof saleEndTime === 'string' && !isNaN(Date.parse(saleEndTime)) ? Date.parse(saleEndTime) : undefined,
      tokenDistributionTime: typeof tokenDistribution === 'string' && !isNaN(Date.parse(tokenDistribution)) ? Date.parse(tokenDistribution) : undefined,
      saleStartTime: typeof saleStartTime === 'string' && !isNaN(Date.parse(saleStartTime)) ? Date.parse(saleStartTime) : undefined
    },
    fee: typeof fee === 'number' ? fee : 0,
    maxAllocation: serverCurrencyToSystemCurrency(maxAllocation),
    minAllocation: serverCurrencyToSystemCurrency(minAllocation),
    buyingProcessPercent: typeof buyingProcessPercent === 'number' ? buyingProcessPercent : 0,
    pTicketBusd: typeof pTicketBusd === 'number' ? pTicketBusd : 0
  };
};
export const parseFusionSeason = data => {
  const {
    id,
    created,
    lastModified,
    cashback,
    startTime,
    endTime,
    skinPool,
    poolExpired
  } = data || {};
  return {
    id: typeof id === 'number' ? id : 0,
    created: typeof created === 'string' && !isNaN(Date.parse(created)) ? Date.parse(created) : undefined,
    lastModified: typeof lastModified === 'string' && !isNaN(Date.parse(lastModified)) ? Date.parse(lastModified) : undefined,
    cashback: Array.isArray(cashback) ? cashback.map(cb => ({
      rate: typeof (cb === null || cb === void 0 ? void 0 : cb.rate) === 'number' ? cb.rate : 0,
      reward: serverCurrencyToSystemCurrency(cb === null || cb === void 0 ? void 0 : cb.reward),
      spend: serverCurrencyToSystemCurrency(cb === null || cb === void 0 ? void 0 : cb.spend)
    })) : [],
    skinPool: Array.isArray(skinPool) ? skinPool.map(sp => typeof sp === 'number' ? sp : 0) : [],
    endTime: typeof endTime === 'string' && !isNaN(Date.parse(endTime)) ? Date.parse(endTime) : undefined,
    startTime: typeof startTime === 'string' && !isNaN(Date.parse(startTime)) ? Date.parse(startTime) : undefined,
    poolExpired: typeof poolExpired === 'string' && !isNaN(Date.parse(poolExpired)) ? Date.parse(poolExpired) : undefined
  };
};
export const parseBattleCapHeroData = data => {
  if (data && typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      acc[Number(key)] = parseBattleCapSkinFusion(data[key]);
      return acc;
    }, {});
  }

  return undefined;
};
export const parseBattleCapSkinFusion = skinConfig => {
  if (skinConfig && typeof skinConfig === 'object') {
    return Object.keys(skinConfig).reduce((acc, key) => {
      var _skinConfig$key, _skinConfig$key$battl, _skinConfig$key2, _skinConfig$key2$batt, _skinConfig$key3, _skinConfig$key3$batt, _skinConfig$key4, _skinConfig$key4$batt;

      acc[Number(key)] = {
        maxGTHCBattles: typeof ((_skinConfig$key = skinConfig[key]) === null || _skinConfig$key === void 0 ? void 0 : (_skinConfig$key$battl = _skinConfig$key.battleCap) === null || _skinConfig$key$battl === void 0 ? void 0 : _skinConfig$key$battl.max) === 'number' ? Number((_skinConfig$key2 = skinConfig[key]) === null || _skinConfig$key2 === void 0 ? void 0 : (_skinConfig$key2$batt = _skinConfig$key2.battleCap) === null || _skinConfig$key2$batt === void 0 ? void 0 : _skinConfig$key2$batt.max) : 0,
        minGTHCBattles: typeof ((_skinConfig$key3 = skinConfig[key]) === null || _skinConfig$key3 === void 0 ? void 0 : (_skinConfig$key3$batt = _skinConfig$key3.battleCap) === null || _skinConfig$key3$batt === void 0 ? void 0 : _skinConfig$key3$batt.min) === 'number' ? Number((_skinConfig$key4 = skinConfig[key]) === null || _skinConfig$key4 === void 0 ? void 0 : (_skinConfig$key4$batt = _skinConfig$key4.battleCap) === null || _skinConfig$key4$batt === void 0 ? void 0 : _skinConfig$key4$batt.min) : 0
      };
      return acc;
    }, {});
  }

  return {
    [-1]: {
      maxGTHCBattles: 0,
      minGTHCBattles: 0
    }
  };
};
export const parseFusionLeaderboardData = leaderboardData => {
  const {
    profileInfo,
    score,
    rank,
    reward
  } = leaderboardData || {};
  const {
    userId,
    username,
    trophy,
    trophyLevel,
    avatarId,
    frameId,
    guildName
  } = profileInfo || {};
  return {
    profileInfo: {
      userId: typeof userId === 'string' ? userId : '',
      username: typeof username === 'string' ? username : '',
      trophy: typeof trophy === 'number' ? trophy : 0,
      trophyLevel: typeof trophyLevel === 'number' ? trophyLevel : 0,
      avatarId: typeof avatarId === 'number' ? avatarId : 0,
      frameId: typeof frameId === 'number' ? frameId : 0,
      guildName: typeof guildName === 'string' ? guildName : ''
    },
    score: typeof score === 'number' ? {
      token: CurrencyType.THC,
      value: score
    } : {
      token: CurrencyType.THC,
      value: ''
    },
    rank: typeof rank === 'number' ? rank : 0,
    reward: reward ? serverCurrencyToSystemCurrency(reward) : {
      token: CurrencyType.GTHG,
      value: ''
    }
  };
};
export const parseSpecialEventProfile = data => {
  const {
    eventType,
    startsIn,
    endsIn,
    eventName,
    eventDescription,
    eventURLBackground
  } = data || {};
  return {
    eventType: typeof eventType === 'number' ? eventType : 0,
    startsIn: typeof startsIn === 'number' ? startsIn : 0,
    endsIn: typeof endsIn === 'number' ? endsIn : 0,
    eventName: typeof eventName === 'string' ? eventName : '',
    eventDescription: typeof eventDescription === 'string' ? eventDescription : '',
    eventURLBackground: typeof eventURLBackground === 'string' ? eventURLBackground : ''
  };
};
export const parseQuestCountInStage = data => {
  const {
    stages,
    defaultQuestCount
  } = data || {};
  return {
    defaultQuestCount: typeof defaultQuestCount === 'number' ? defaultQuestCount : 0,
    stages: typeof stages === 'object' ? Object.keys(stages).reduce((acc, key) => {
      const current = stages[key] || 0;
      acc[Number(key)] = current;
      return acc;
    }, {}) : {}
  };
};
export const parseMyQuestStagesData = data => {
  if (data && typeof data === 'object') {
    return Object.keys(data).reduce((acc, key) => {
      const current = data[key] || {};
      acc[Number(key)] = Array.isArray(current) ? current.map(({
        questId,
        amount,
        currentAmount,
        questExtraData,
        priceRefresh
      }) => {
        return {
          questId: typeof questId === 'number' ? questId : 0,
          amount: typeof amount === 'number' && amount > 0 ? amount : 1,
          currentAmount: typeof currentAmount === 'number' ? currentAmount : 0,
          questExtraData: typeof questExtraData === 'object' ? {
            type: typeof (questExtraData === null || questExtraData === void 0 ? void 0 : questExtraData.type) === 'number' ? questExtraData.type : 0,
            value: typeof (questExtraData === null || questExtraData === void 0 ? void 0 : questExtraData.value) === 'number' ? questExtraData.value : 0
          } : undefined,
          priceRefresh: typeof priceRefresh === 'object' ? serverCurrencyToSystemCurrency(priceRefresh) : undefined
        };
      }) : [];
      return acc;
    }, {});
  }

  return undefined;
};
export const parseQuestPointEachStageData = data => {
  if (data && typeof data === 'object') return Object.keys(data).reduce((acc, key) => {
    const current = data[key];
    if (current) acc[Number(key)] = serverCurrencyToSystemCurrency(current);
    return acc;
  }, {});
  return undefined;
};
export const parseStagesRewardData = data => {
  if (data && typeof data === 'object') return Object.keys(data).reduce((acc, key) => {
    var _data$key, _data$key2;

    acc[Number(key)] = {
      claimed: typeof ((_data$key = data[key]) === null || _data$key === void 0 ? void 0 : _data$key.claimed) === 'boolean' ? (_data$key2 = data[key]) === null || _data$key2 === void 0 ? void 0 : _data$key2.claimed : false
    };
    return acc;
  }, {});
  return undefined;
};
export const parseQuestLeaderboardData = leaderboardData => {
  const {
    profileInfo,
    score,
    rank,
    reward
  } = leaderboardData || {};
  const {
    userId,
    username,
    trophy,
    trophyLevel,
    avatarId,
    frameId,
    guildName
  } = profileInfo || {};
  return {
    profileInfo: {
      userId: typeof userId === 'string' ? userId : '',
      username: typeof username === 'string' ? username : '',
      trophy: typeof trophy === 'number' ? trophy : 0,
      trophyLevel: typeof trophyLevel === 'number' ? trophyLevel : 0,
      avatarId: typeof avatarId === 'number' ? avatarId : 0,
      frameId: typeof frameId === 'number' ? frameId : 0,
      guildName: typeof guildName === 'string' ? guildName : ''
    },
    score: typeof score === 'number' ? score : 0,
    rank: typeof rank === 'number' ? rank : 0,
    reward: reward ? serverCurrencyToSystemCurrency(reward) : {
      token: CurrencyType.GMQP,
      value: ''
    }
  };
};
export const parseDailyQuestReward = rewardData => {
  const {
    autoClaimReward,
    loginStreakReward,
    dailyReward
  } = rewardData || {};
  const {
    isReset,
    reward: autoReward
  } = autoClaimReward || {};
  const {
    currentStreak,
    maxStreak,
    streakClaimStatus,
    currencyReward
  } = loginStreakReward || {};
  const {
    expiredTime,
    nextRewardIn,
    claimStatus,
    reward,
    nextReward
  } = dailyReward || {};
  return {
    dailyReward: {
      expiredTime: typeof expiredTime === 'number' ? expiredTime : undefined,
      claimStatus: typeof claimStatus === 'number' ? claimStatus : QuestClaimStatus.CAN_CLAIM,
      nextReward: serverCurrencyToSystemCurrency(nextReward),
      nextRewardIn: typeof nextRewardIn === 'number' ? nextRewardIn : undefined,
      reward: serverCurrencyToSystemCurrency(reward)
    },
    loginStreakReward: {
      currentStreak: typeof currentStreak === 'number' ? currentStreak : 0,
      maxStreak: typeof maxStreak === 'number' ? maxStreak : 0,
      streakClaimStatus: typeof streakClaimStatus === 'number' ? streakClaimStatus : QuestClaimStatus.CAN_CLAIM,
      currencyReward: serverCurrencyToSystemCurrency(currencyReward)
    },
    autoClaimReward: autoClaimReward !== undefined ? {
      isReset: typeof isReset === 'boolean' ? isReset : false,
      reward: autoReward ? serverCurrencyToSystemCurrency(autoReward) : undefined
    } : undefined
  };
};
export const parseQuestBox = questBoxData => {
  const {
    price,
    nextTimeClaim,
    config
  } = questBoxData || {};
  const {
    boxType,
    name,
    color,
    blurColor,
    textColor,
    imgPath,
    bgPath,
    ribbonPath,
    questRewards
  } = config || {};
  return {
    nextTimeClaim: typeof nextTimeClaim === 'number' ? nextTimeClaim : undefined,
    price: serverCurrencyToSystemCurrency(price),
    config: {
      boxType: typeof boxType === 'number' ? boxType : -1,
      name: typeof name === 'string' ? name : '',
      color: typeof color === 'string' ? color : '',
      blurColor: typeof blurColor === 'string' ? blurColor : '',
      textColor: typeof textColor === 'string' ? textColor : '',
      imgPath: typeof imgPath === 'string' ? webImageUri(imgPath) : '',
      bgPath: typeof bgPath === 'string' ? webImageUri(bgPath) : '',
      ribbonPath: typeof ribbonPath === 'string' ? webImageUri(ribbonPath) : '',
      questRewards: Array.isArray(questRewards) ? questRewards.map(item => {
        return {
          type: typeof (item === null || item === void 0 ? void 0 : item.type) === 'number' ? item.type : 0,
          rate: typeof (item === null || item === void 0 ? void 0 : item.rate) === 'number' ? item.rate : 0
        };
      }) : []
    }
  };
};
export const parseNftHeroRecently = hero => {
  const {
    id,
    ownerId,
    tokenId,
    refId,
    refType,
    systemCurrency,
    hp,
    dmg,
    heroTypeId,
    heroRarity,
    skinId,
    skinRarity,
    status,
    marketStatus,
    level,
    trophyClass,
    imageAvatar,
    imageFull,
    name,
    skinName,
    rarity,
    heroRole,
    ownerAddress,
    battleCap,
    battleCapMax,
    thcBonus,
    dailyTHCBattleConfig,
    timestamp,
    rentOutInfo
  } = hero;
  return {
    id: typeof id === 'string' ? id : '',
    ownerId: typeof ownerId === 'string' ? ownerId : '',
    tokenId: typeof tokenId === 'string' ? tokenId : '',
    refId: typeof refId === 'string' ? refId : '',
    refType: typeof refType === 'string' ? refType : NftRefType.HERO,
    systemPrice: systemCurrency ? serverCurrencyToSystemCurrency(systemCurrency) : undefined,
    hp: typeof hp === 'number' ? hp : 0,
    dmg: typeof dmg === 'number' ? dmg : 0,
    heroTypeId: typeof heroTypeId === 'number' ? heroTypeId : 0,
    heroRarity: typeof heroRarity === 'number' ? heroRarity : NftItemRarity.COMMON,
    skinId: typeof skinId === 'number' ? skinId : 0,
    skinRarity: typeof skinRarity === 'number' ? skinRarity : NftSkinItemRarity.NORMAL,
    status: typeof status === 'number' ? status : HeroStatus.DEFAULT,
    marketStatus: typeof marketStatus === 'number' ? marketStatus : 0,
    level: typeof level === 'number' ? level : 0,
    trophy: typeof trophyClass === 'number' ? trophyClass : HeroTrophy.H,
    imageAvatar: typeof imageAvatar === 'string' ? imageAvatar : '',
    imageFull: typeof imageFull === 'string' ? imageFull : '',
    name: typeof name === 'string' ? name : '',
    skinName: typeof skinName === 'string' ? skinName : '',
    rarity: typeof rarity === 'number' ? rarity : NftItemRarity.COMMON,
    heroRole: typeof heroRole === 'number' ? heroRole : NtfHeroClassType.TANK,
    ownerAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    battleCap: typeof battleCap === 'number' ? battleCap : 0,
    battleCapMax: typeof battleCapMax === 'number' ? battleCapMax : 0,
    thcBonus: typeof thcBonus === 'number' ? thcBonus : 0,
    dailyTHCBattleConfig: typeof dailyTHCBattleConfig === 'number' ? dailyTHCBattleConfig : 0,
    timestamp: typeof timestamp === 'string' && !isNaN(Date.parse(timestamp)) ? Date.parse(timestamp) : 0,
    rentOutInfo: rentOutInfo ? {
      rentBattles: typeof rentOutInfo.rentBattles === 'number' ? rentOutInfo.rentBattles : 0
    } : undefined
  };
};
export const parseNftCosmeticRecently = cosmetic => {
  const {
    id,
    createdAt,
    lastModified,
    ownerAddress,
    refId,
    refType,
    tokenId,
    cosmeticInfo,
    saleInfo,
    cosmeticRarity,
    cosmeticType,
    cosmeticName,
    nftId,
    timestamp,
    blockchainId
  } = cosmetic;
  return {
    blockchainId: typeof blockchainId === 'number' ? blockchainId : 0,
    id: typeof id === 'string' ? id : '',
    cosmeticInfo: {
      status: typeof cosmeticInfo.status === 'number' ? cosmeticInfo.status : CosmeticStatus.AVAILABLE,
      typeId: typeof cosmeticInfo.typeId === 'number' ? cosmeticInfo.typeId : 0,
      type: typeof cosmeticInfo.type === 'number' ? cosmeticInfo.type : 0,
      rarity: typeof cosmeticInfo.rarity === 'number' ? cosmeticInfo.rarity : 0
    },
    createdAt: typeof createdAt === 'string' ? createdAt : '',
    lastModified: typeof lastModified === 'string' ? lastModified : '',
    refId: typeof refId === 'string' ? refId : '',
    nftId: typeof refId === 'string' ? nftId : '',
    refType: typeof refType === 'number' ? refType : CosmeticRefType.COSMETIC,
    tokenId: typeof tokenId === 'number' ? tokenId : 0,
    saleInfo: {
      price: serverCurrencyToSystemCurrency(saleInfo.price)
    },
    ownerAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    userAddress: typeof ownerAddress === 'string' ? ownerAddress : '',
    cosmeticRarity: typeof cosmeticRarity === 'number' ? cosmeticRarity : CosmeticRarity.COMMON,
    cosmeticType: typeof cosmeticType === 'number' ? cosmeticType : undefined,
    cosmeticName: typeof cosmeticName === 'string' ? cosmeticName : '',
    timestamp: typeof timestamp === 'string' && !isNaN(Date.parse(timestamp)) ? Date.parse(timestamp) : 0
  };
};
export const parseTradingEventWarmup = data => {
  const {
    type,
    skinRarity,
    heroClass,
    heroLevel,
    heroTrophyClass
  } = data || {};
  let valueData;

  if (skinRarity) {
    valueData = skinRarity;
  } else if (heroClass) {
    valueData = heroClass;
  } else if (heroLevel) {
    valueData = heroLevel;
  } else if (heroTrophyClass) {
    valueData = heroTrophyClass;
  }

  return {
    type: typeof type === 'number' ? type : TradingQuestType.HERO_TROPHY_CLASS,
    value: typeof valueData === 'number' ? valueData : 0
  };
};
export const parsePopupConfig = config => {
  const {
    centerPopup,
    cornerPopup
  } = config || {};
  return {
    centerPopup: Array.isArray(centerPopup) ? centerPopup.map(popup => ({
      dismissDurationInSecond: typeof popup.dismissDurationInSecond === 'number' ? popup.dismissDurationInSecond : 0,
      image: typeof popup.image === 'string' ? popup.image : '',
      name: typeof popup.name === 'string' ? popup.name : '',
      navigateTo: typeof popup.navigateTo === 'string' ? popup.navigateTo : '',
      periodEndTime: typeof popup.periodEndTime === 'string' ? new Date(popup.periodEndTime).getTime() / 1000 : 0,
      periodStartTime: typeof popup.periodStartTime === 'string' ? new Date(popup.periodStartTime).getTime() / 1000 : 0,
      sortOrder: typeof popup.sortOrder === 'number' ? popup.sortOrder : 0,
      tabs: popup.tabs && Array.isArray(popup.tabs) ? popup.tabs.map(tab => typeof tab === 'string' ? tab : '') : []
    })) : [],
    cornerPopup: typeof cornerPopup !== undefined ? {
      dismissDurationInSecond: typeof (cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.dismissDurationInSecond) === 'number' ? cornerPopup.dismissDurationInSecond : 0,
      image: typeof (cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.image) === 'string' ? cornerPopup.image : '',
      name: typeof (cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.name) === 'string' ? cornerPopup.name : '',
      navigateTo: typeof (cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.navigateTo) === 'string' ? cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.navigateTo : '',
      periodEndTime: typeof (cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.periodEndTime) === 'string' ? new Date(cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.periodEndTime).getTime() / 1000 : 0,
      periodStartTime: typeof (cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.periodStartTime) === 'string' ? new Date(cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.periodStartTime).getTime() / 1000 : 0,
      tabs: cornerPopup !== null && cornerPopup !== void 0 && cornerPopup.tabs && Array.isArray(cornerPopup === null || cornerPopup === void 0 ? void 0 : cornerPopup.tabs) ? cornerPopup.tabs.map(tab => typeof tab === 'string' ? tab : '') : []
    } : undefined
  };
};
export const parseGiftmasWheel = holidayWheel => {
  const {
    userWheelData,
    wheelConfig,
    wheelQuestConfig,
    combineRewardConfig,
    eventProfile
  } = (holidayWheel === null || holidayWheel === void 0 ? void 0 : holidayWheel.data) || {};
  const {
    lockedTickets,
    availableTickets,
    ticketStatus,
    wheelRewards,
    quests,
    combineRewards,
    spinInfo
  } = userWheelData || {};
  const {
    DailyCheckin,
    FriendSequence,
    Christmas,
    Sequence,
    Wallet
  } = quests || {};
  const {
    wheelType,
    wheelSlots,
    wheelSpinLimit,
    ticketNumberPerSpin
  } = wheelConfig || {};
  const {
    wheelQuests
  } = wheelQuestConfig || {};
  const {
    inputItems,
    outputOneOfItems
  } = combineRewardConfig || {};
  return {
    eventProfile: {
      startTime: typeof (eventProfile === null || eventProfile === void 0 ? void 0 : eventProfile.startTime) === 'string' && !isNaN(Date.parse(eventProfile.startTime)) ? Date.parse(eventProfile.startTime) : 0,
      endTime: typeof (eventProfile === null || eventProfile === void 0 ? void 0 : eventProfile.endTime) === 'string' && !isNaN(Date.parse(eventProfile.endTime)) ? Date.parse(eventProfile.endTime) : 0
    },
    userWheelData: {
      lockedTickets: typeof lockedTickets === 'number' ? lockedTickets : 0,
      availableTickets: typeof availableTickets === 'number' ? availableTickets : 0,
      ticketStatus: typeof ticketStatus === 'string' ? ticketStatus : ClaimTicketStatus.AVAILABLE,
      wheelRewards: Array.isArray(wheelRewards) ? wheelRewards.map(reward => ({
        name: typeof reward.name === 'string' ? reward.name : '',
        rewardType: typeof reward.rewardType === 'string' ? reward.rewardType : RewardType.HERO,
        subId: typeof reward.subId === 'number' ? reward.subId : 0,
        receivedTime: typeof reward.receivedTime === 'string' ? reward.receivedTime : '',
        amount: typeof reward.amount === 'number' ? reward.amount : 0,
        isTitle: typeof reward.isTitle === 'boolean' ? reward.isTitle : false,
        status: typeof reward.status === 'number' ? reward.status : 0
      })) : [],
      quests: {
        DailyCheckin: {
          doneQuestCount: typeof (DailyCheckin === null || DailyCheckin === void 0 ? void 0 : DailyCheckin.doneQuestCount) === 'number' ? DailyCheckin.doneQuestCount : 0,
          doQuestCount: typeof (DailyCheckin === null || DailyCheckin === void 0 ? void 0 : DailyCheckin.doQuestCount) === 'number' ? DailyCheckin.doQuestCount : 0,
          refreshTime: typeof (DailyCheckin === null || DailyCheckin === void 0 ? void 0 : DailyCheckin.refreshTime) === 'string' && !isNaN(Date.parse(DailyCheckin.refreshTime)) ? Date.parse(DailyCheckin.refreshTime) : 0,
          receivedTickets: typeof (DailyCheckin === null || DailyCheckin === void 0 ? void 0 : DailyCheckin.receivedTickets) === 'string' ? DailyCheckin.receivedTickets : ''
        },
        FriendSequence: {
          doneQuestCount: typeof (FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.doneQuestCount) === 'number' ? FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.doneQuestCount : 0,
          doQuestCount: typeof (FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.doQuestCount) === 'number' ? FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.doQuestCount : 0,
          refreshTime: typeof (FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.refreshTime) === 'string' && !isNaN(Date.parse(FriendSequence.refreshTime)) ? Date.parse(FriendSequence.refreshTime) : 0,
          receivedTickets: typeof (FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.receivedTickets) === 'string' ? FriendSequence === null || FriendSequence === void 0 ? void 0 : FriendSequence.receivedTickets : ''
        },
        Christmas: {
          doneQuestCount: typeof (Christmas === null || Christmas === void 0 ? void 0 : Christmas.doneQuestCount) === 'number' ? Christmas === null || Christmas === void 0 ? void 0 : Christmas.doneQuestCount : 0,
          doQuestCount: typeof (Christmas === null || Christmas === void 0 ? void 0 : Christmas.doQuestCount) === 'number' ? Christmas === null || Christmas === void 0 ? void 0 : Christmas.doQuestCount : 0,
          refreshTime: typeof (Christmas === null || Christmas === void 0 ? void 0 : Christmas.refreshTime) === 'string' && !isNaN(Date.parse(Christmas.refreshTime)) ? Date.parse(Christmas.refreshTime) : 0,
          receivedTickets: typeof (Christmas === null || Christmas === void 0 ? void 0 : Christmas.receivedTickets) === 'string' ? Christmas === null || Christmas === void 0 ? void 0 : Christmas.receivedTickets : ''
        },
        Sequence: {
          doneQuestCount: typeof (Sequence === null || Sequence === void 0 ? void 0 : Sequence.doneQuestCount) === 'number' ? Sequence === null || Sequence === void 0 ? void 0 : Sequence.doneQuestCount : 0,
          doQuestCount: typeof (Sequence === null || Sequence === void 0 ? void 0 : Sequence.doQuestCount) === 'number' ? Sequence === null || Sequence === void 0 ? void 0 : Sequence.doQuestCount : 0,
          refreshTime: typeof (Sequence === null || Sequence === void 0 ? void 0 : Sequence.refreshTime) === 'string' && !isNaN(Date.parse(Sequence.refreshTime)) ? Date.parse(Sequence.refreshTime) : 0,
          receivedTickets: typeof (Sequence === null || Sequence === void 0 ? void 0 : Sequence.receivedTickets) === 'string' ? Sequence === null || Sequence === void 0 ? void 0 : Sequence.receivedTickets : ''
        },
        Wallet: {
          doneQuestCount: typeof (Wallet === null || Wallet === void 0 ? void 0 : Wallet.doneQuestCount) === 'number' ? Wallet === null || Wallet === void 0 ? void 0 : Wallet.doneQuestCount : 0,
          doQuestCount: typeof (Wallet === null || Wallet === void 0 ? void 0 : Wallet.doQuestCount) === 'number' ? Wallet === null || Wallet === void 0 ? void 0 : Wallet.doQuestCount : 0,
          refreshTime: typeof (Wallet === null || Wallet === void 0 ? void 0 : Wallet.refreshTime) === 'string' && !isNaN(Date.parse(Wallet.refreshTime)) ? Date.parse(Wallet.refreshTime) : 0,
          receivedTickets: typeof (Wallet === null || Wallet === void 0 ? void 0 : Wallet.receivedTickets) === 'string' ? Wallet === null || Wallet === void 0 ? void 0 : Wallet.receivedTickets : ''
        }
      },
      combineRewards: Array.isArray(combineRewards) ? combineRewards.map(combine => ({
        rewardType: typeof combine.rewardType === 'string' ? combine.rewardType : RewardType.HERO,
        subId: typeof combine.subId === 'number' ? combine.subId : 0,
        amount: typeof combine.amount === 'number' ? combine.amount : 0,
        receivedTime: typeof combine.receivedTime === 'string' ? combine.receivedTime : ''
      })) : [],
      spinInfo: {
        spinNumber: typeof (spinInfo === null || spinInfo === void 0 ? void 0 : spinInfo.spinNumber) === 'number' ? spinInfo.spinNumber : 0,
        totalSpinNumber: typeof (spinInfo === null || spinInfo === void 0 ? void 0 : spinInfo.totalSpinNumber) === 'number' ? spinInfo.totalSpinNumber : 0,
        refreshTime: typeof (spinInfo === null || spinInfo === void 0 ? void 0 : spinInfo.refreshTime) === 'string' && !isNaN(Date.parse(spinInfo.refreshTime)) ? Date.parse(spinInfo.refreshTime) : 0
      }
    },
    wheelConfig: {
      wheelType: typeof wheelType === 'string' ? wheelType : WheelType.Wheel1,
      wheelSlots: Array.isArray(wheelSlots) ? wheelSlots.map(slot => ({
        wheelRewards: Array.isArray(slot.wheelRewards) ? slot.wheelRewards.map(reward => ({
          name: typeof reward.name === 'string' ? reward.name : '',
          rewardType: typeof reward.rewardType === 'string' ? reward.rewardType : RewardType.HERO,
          subIds: Array.isArray(reward.subIds) ? reward.subIds : [],
          receivedTime: typeof reward.receivedTime === 'string' ? reward.receivedTime : '',
          amount: typeof reward.amount === 'number' ? reward.amount : 0,
          isTitle: typeof reward.isTitle === 'boolean' ? reward.isTitle : false
        })) : []
      })) : [],
      wheelSpinLimit: {
        type: typeof (wheelSpinLimit === null || wheelSpinLimit === void 0 ? void 0 : wheelSpinLimit.type) === 'string' ? wheelSpinLimit.type : ClaimLimitType.DAILY,
        maxNumber: typeof (wheelSpinLimit === null || wheelSpinLimit === void 0 ? void 0 : wheelSpinLimit.maxNumber) === 'number' ? wheelSpinLimit.maxNumber : 0
      },
      ticketNumberPerSpin: typeof ticketNumberPerSpin === 'number' ? ticketNumberPerSpin : 0
    },
    wheelQuestConfig: {
      wheelQuests: Array.isArray(wheelQuests) ? wheelQuests.map(quest => ({
        questType: typeof quest.questType === 'string' ? quest.questType : GiftmasQuestType.DAILY,
        questName: typeof quest.questName === 'string' ? quest.questName : '',
        activeFlag: typeof quest.activeFlag === 'boolean' ? quest.activeFlag : false,
        ticketCount: typeof quest.ticketCount === 'number' ? quest.ticketCount : 0,
        limit: {
          type: typeof quest.limit.type === 'string' ? quest.limit.type : ClaimLimitType.WEEKLY,
          maxNumber: typeof quest.limit.maxNumber === 'number' ? quest.limit.maxNumber : 0
        },
        startTime: typeof quest.startTime === 'string' && !isNaN(Date.parse(quest.startTime)) ? Date.parse(quest.startTime) : 0,
        endTime: typeof quest.endTime === 'string' && !isNaN(Date.parse(quest.endTime)) ? Date.parse(quest.endTime) : 0
      })) : []
    },
    combineRewardConfig: {
      inputItems: Array.isArray(inputItems) ? inputItems.map(reward => ({
        name: typeof reward.name === 'string' ? reward.name : '',
        rewardType: typeof reward.rewardType === 'string' ? reward.rewardType : RewardType.HERO,
        subIds: Array.isArray(reward.subIds) ? reward.subIds : [],
        amount: typeof reward.amount === 'number' ? reward.amount : 0
      })) : [],
      outputOneOfItems: Array.isArray(outputOneOfItems) ? outputOneOfItems.map(reward => ({
        name: typeof reward.name === 'string' ? reward.name : '',
        rewardType: typeof reward.rewardType === 'string' ? reward.rewardType : RewardType.HERO,
        subIds: Array.isArray(reward.subIds) ? reward.subIds : [],
        amount: typeof reward.amount === 'number' ? reward.amount : 0
      })) : []
    }
  };
};